/**
 * Projects MVI Effects
 *
 * Side effects: API calls, dispatches intents on completion.
 */

import type { ICreateProjectRequest } from "@/shared/api";
import type { IApiError } from "@/shared/types";
import { getProjectsApiClient } from "@/shared/api";
import { intents } from "./intents";
import type { TProjectsIntent } from "./intents";

type Dispatch = (intent: TProjectsIntent) => void;

function createApiError(error: unknown): IApiError {
  if (
    error !== null &&
    typeof error === "object" &&
    "response" in error &&
    error.response !== null &&
    typeof error.response === "object"
  ) {
    const response = error.response;
    if ("data" in response && response.data !== null && typeof response.data === "object") {
      const data = response.data;
      const code =
        "code" in data && typeof data.code === "string" ? data.code : "UNKNOWN_ERROR";

      let message = "Request failed";
      if ("message" in data && typeof data.message === "string") {
        message = data.message;
      } else if ("error" in data && typeof data.error === "string") {
        message = data.error;
      } else if (error instanceof Error) {
        message = error.message;
      }
      const params =
        "params" in data && typeof data.params === "object" && data.params !== null
          ? (data.params as Record<string, unknown>)
          : undefined;

      return { code, message, params };
    }
  }

  if (error instanceof Error) {
    return { code: "UNKNOWN_ERROR", message: error.message };
  }
  return { code: "UNKNOWN_ERROR", message: String(error) };
}

export async function loadProjects(dispatch: Dispatch): Promise<void> {
  dispatch(intents.loadProjectsStart());
  try {
    const api = getProjectsApiClient();
    const projects = await api.listProjects();
    dispatch(intents.loadProjectsSuccess(projects));
  } catch (error: unknown) {
    dispatch(intents.loadProjectsError(createApiError(error)));
  }
}

export async function createProject(
  dispatch: Dispatch,
  request: ICreateProjectRequest,
): Promise<void> {
  dispatch(intents.createProjectStart());
  try {
    const api = getProjectsApiClient();
    const project = await api.createProject(request);
    dispatch(intents.createProjectSuccess(project));
  } catch (error: unknown) {
    dispatch(intents.createProjectError(createApiError(error)));
  }
}
